import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, ChevronLeft, Mic, Plus, User } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { formatTime } from '@/shared/lib/format';
import { useTheme } from '@/shared/lib/theme';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { formatSmsSender, splitSmsPreview } from '../model/preview';

export type SmsPreviewPlatform = 'ios' | 'android';

/**
 * Conversa como quem recebe vê.
 * SMS de verdade: balão cinza à esquerda. Sem azul de iMessage, sem recibo,
 * reação ou recurso que só existe no RCS.
 */
export const smsPreviewLayoutClass = 'grid items-start gap-6 lg:grid-cols-2 lg:gap-8';

interface SmsPalette {
  screen: string;
  ink: string;
  sub: string;
  bubble: string;
  bubbleInk: string;
  link: string;
  accent: string;
  field: string;
  fieldInk: string;
  hairline: string;
  avatar: string;
  avatarInk: string;
}

const PALETTES: Record<SmsPreviewPlatform, Record<'light' | 'dark', SmsPalette>> = {
  ios: {
    light: {
      screen: '#FFFFFF',
      ink: '#000000',
      sub: '#8E8E93',
      bubble: '#E9E9EB',
      bubbleInk: '#000000',
      link: '#007AFF',
      accent: '#007AFF',
      field: '#E9E9EB',
      fieldInk: '#8E8E93',
      hairline: 'rgba(60, 60, 67, 0.16)',
      avatar: '#E5E5EA',
      avatarInk: '#8E8E93',
    },
    dark: {
      screen: '#000000',
      ink: '#FFFFFF',
      sub: '#8D8D93',
      bubble: '#3A3A3C',
      bubbleInk: '#FFFFFF',
      link: '#0A84FF',
      accent: '#0A84FF',
      field: '#1C1C1E',
      fieldInk: '#8D8D93',
      hairline: 'rgba(255, 255, 255, 0.12)',
      avatar: '#2C2C2E',
      avatarInk: '#8D8D93',
    },
  },
  android: {
    light: {
      screen: '#FFFFFF',
      ink: '#1F1F1F',
      sub: '#5F6368',
      bubble: '#EEEEEE',
      bubbleInk: '#1F1F1F',
      link: '#0B57D0',
      accent: '#0B57D0',
      field: '#F1F3F4',
      fieldInk: '#5F6368',
      hairline: 'rgba(0, 0, 0, 0.08)',
      avatar: '#E8EAED',
      avatarInk: '#5F6368',
    },
    dark: {
      screen: '#131314',
      ink: '#E8EAED',
      sub: '#9AA0A6',
      bubble: '#3C4043',
      bubbleInk: '#E8EAED',
      link: '#A8C7FA',
      accent: '#A8C7FA',
      field: '#2D2F31',
      fieldInk: '#9AA0A6',
      hairline: 'rgba(255, 255, 255, 0.08)',
      avatar: '#3C4043',
      avatarInk: '#E8EAED',
    },
  },
};

function StatusIcons() {
  return (
    <span className="flex items-center gap-[5px]">
      <svg width="16" height="11" viewBox="0 0 16 11" aria-hidden>
        <rect x="0" y="6.5" width="2.6" height="4.5" rx="0.5" fill="currentColor" />
        <rect x="4.2" y="4.4" width="2.6" height="6.6" rx="0.5" fill="currentColor" />
        <rect x="8.4" y="2.2" width="2.6" height="8.8" rx="0.5" fill="currentColor" />
        <rect x="12.6" y="0" width="2.6" height="11" rx="0.5" fill="currentColor" />
      </svg>
      <svg width="14" height="11" viewBox="0 0 14 11" aria-hidden>
        <path
          fill="currentColor"
          d="M7 3.1c1.5 0 2.8.6 3.8 1.6l.9-.9A6.4 6.4 0 0 0 7 2a6.4 6.4 0 0 0-4.7 1.8l.9.9A5.2 5.2 0 0 1 7 3.1Z"
        />
        <path
          fill="currentColor"
          d="M7 6c.9 0 1.7.4 2.3 1l.9-.9a4.4 4.4 0 0 0-6.4 0l.9.9A3.2 3.2 0 0 1 7 6Z"
        />
        <circle cx="7" cy="9.2" r="1.15" fill="currentColor" />
      </svg>
      <svg width="23" height="11" viewBox="0 0 23 11" aria-hidden>
        <rect
          x="0.6"
          y="0.6"
          width="19"
          height="9.8"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.1"
        />
        <rect x="2" y="2" width="14.4" height="7" rx="0.8" fill="currentColor" />
        <path fill="currentColor" d="M21 3.6v3.8c.7-.3 1.2-.9 1.2-1.9s-.5-1.6-1.2-1.9Z" />
      </svg>
    </span>
  );
}

function MessageText({
  text,
  platform,
  palette,
}: {
  text: string;
  platform: SmsPreviewPlatform;
  palette: SmsPalette;
}) {
  const pieces = splitSmsPreview(text);
  return (
    <p
      className="whitespace-pre-wrap break-words"
      style={{
        fontSize: platform === 'ios' ? 16 : 15,
        lineHeight: platform === 'ios' ? '21px' : '20px',
        letterSpacing: platform === 'ios' ? '-0.012em' : '0.01em',
      }}
    >
      {pieces.map((piece, index) => {
        if (piece.kind === 'link') {
          return (
            <span
              key={index}
              className="underline decoration-1 underline-offset-[3px]"
              style={{ color: palette.link }}
            >
              {piece.value}
            </span>
          );
        }
        if (piece.kind === 'token') {
          return (
            <span
              key={index}
              className="font-medium underline decoration-dotted underline-offset-[3px]"
            >
              {piece.value}
            </span>
          );
        }
        return <span key={index}>{piece.value}</span>;
      })}
    </p>
  );
}

function IncomingBubble({
  platform,
  palette,
  children,
}: {
  platform: SmsPreviewPlatform;
  palette: SmsPalette;
  children: ReactNode;
}) {
  const ios = platform === 'ios';
  return (
    <div className={cn('relative w-fit max-w-[84%]', ios && 'ml-1.5')}>
      <div
        className="relative z-[1] px-3 py-[7px]"
        style={{
          background: palette.bubble,
          color: palette.bubbleInk,
          borderRadius: 18,
          borderBottomLeftRadius: ios ? 18 : 5,
        }}
      >
        {children}
      </div>
      {ios ? (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute bottom-0 z-0 h-[17px] w-[17px]"
            style={{ left: -6, background: palette.bubble, borderBottomLeftRadius: 14 }}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute bottom-0 z-[2] h-[17px] w-[9px]"
            style={{ left: -9, background: palette.screen, borderBottomRightRadius: 10 }}
          />
        </>
      ) : null}
    </div>
  );
}

function PhoneHeader({
  platform,
  sender,
  palette,
}: {
  platform: SmsPreviewPlatform;
  sender: string;
  palette: SmsPalette;
}) {
  if (platform === 'android') {
    return (
      <header
        className="flex shrink-0 items-center gap-1 px-1 pb-2 pt-1"
        style={{ boxShadow: `inset 0 -0.5px 0 ${palette.hairline}` }}
      >
        <span className="grid size-9 shrink-0 place-items-center" aria-hidden>
          <ArrowLeft className="size-5" strokeWidth={2} />
        </span>
        <span
          aria-hidden
          className="grid size-9 shrink-0 place-items-center rounded-full"
          style={{ background: palette.avatar, color: palette.avatarInk }}
        >
          <User className="size-[18px]" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1 pr-3">
          <p className="truncate text-[15px] font-medium leading-tight tracking-tight">{sender}</p>
          <p className="text-[12px] leading-tight" style={{ color: palette.sub }}>
            SMS
          </p>
        </div>
      </header>
    );
  }

  return (
    <header
      className="shrink-0 px-1 pb-2.5 pt-0.5"
      style={{ boxShadow: `inset 0 -0.5px 0 ${palette.hairline}` }}
    >
      <div className="grid grid-cols-[2.25rem_minmax(0,1fr)_2.25rem] items-center">
        <span className="grid h-8 place-items-center" style={{ color: palette.accent }} aria-hidden>
          <ChevronLeft className="size-6" strokeWidth={2.4} />
        </span>
        <div className="flex min-w-0 flex-col items-center">
          <span
            aria-hidden
            className="grid size-8 place-items-center rounded-full"
            style={{ background: palette.avatar, color: palette.avatarInk }}
          >
            <User className="size-4" strokeWidth={1.75} />
          </span>
          <p className="mt-1 max-w-full truncate px-1 text-[13px] font-semibold leading-none tracking-tight">
            {sender}
          </p>
          <p className="mt-1 text-[11px] leading-none" style={{ color: palette.sub }}>
            Mensagem de texto
          </p>
        </div>
      </div>
    </header>
  );
}

function ComposerBar({ platform, palette }: { platform: SmsPreviewPlatform; palette: SmsPalette }) {
  return (
    <div
      aria-hidden
      className="flex shrink-0 items-center gap-2 px-2.5 pb-3 pt-2"
      style={{ boxShadow: `inset 0 0.5px 0 ${palette.hairline}` }}
    >
      <span
        className="grid size-8 shrink-0 place-items-center"
        style={{ color: platform === 'ios' ? palette.accent : palette.ink }}
      >
        <Plus className="size-5" strokeWidth={2} />
      </span>
      <div
        className="flex h-9 min-w-0 flex-1 items-center rounded-full px-3.5"
        style={{ background: palette.field }}
      >
        <span className="truncate text-[15px]" style={{ color: palette.fieldInk }}>
          {platform === 'ios' ? 'Mensagem de texto' : 'Mensagem SMS'}
        </span>
        <Mic className="ml-auto size-4 shrink-0" style={{ color: palette.sub }} />
      </div>
    </div>
  );
}

export function SmsPhonePreview({
  message,
  sender,
  followTyping = false,
  framed = false,
  className,
}: {
  message: string;
  sender: string;
  /** Acompanha o fim do texto enquanto a pessoa escreve. */
  followTyping?: boolean;
  /** Cabeçalho na largura da coluna e o aparelho centralizado num palco. */
  framed?: boolean;
  className?: string;
}) {
  const { resolvedTheme } = useTheme();
  const [platform, setPlatform] = useState<SmsPreviewPlatform>('ios');
  const threadRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);
  const palette = PALETTES[platform][resolvedTheme];
  const senderLabel = formatSmsSender(sender);
  const clock = useMemo(() => formatTime(new Date()), []);
  const trimmed = message.trim();

  useEffect(() => {
    const el = threadRef.current;
    if (!el || !followTyping || !pinnedRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [message, platform, followTyping]);

  return (
    <section
      aria-label="Prévia do SMS no celular"
      className={cn('flex w-full min-w-0 flex-col', className)}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="min-w-0 text-sm font-medium">Prévia</p>
        <SegmentedControl
          size="sm"
          className="shrink-0"
          value={platform}
          onChange={setPlatform}
          options={[
            { value: 'ios', label: 'iOS' },
            { value: 'android', label: 'Android' },
          ]}
        />
      </div>

      <div
        className={cn(
          framed &&
            'flex min-h-0 flex-1 items-center justify-center rounded-xl bg-foreground/[0.04] px-3 py-6 sm:px-4',
        )}
      >
        <div
          data-platform={platform}
          className="mx-auto flex h-[28rem] w-full max-w-[20rem] flex-col overflow-hidden rounded-[1.5rem] shadow-glass-lg ring-1 ring-black/10 dark:ring-white/15 sm:h-[30.5rem]"
          style={{ background: palette.screen, color: palette.ink }}
        >
          <div aria-hidden className="flex shrink-0 items-center justify-between px-5 pb-1 pt-3">
            <span
              className={cn(
                'tabular-nums leading-none',
                platform === 'ios'
                  ? 'text-[14px] font-semibold tracking-tight'
                  : 'text-[12px] font-medium',
              )}
            >
              {clock}
            </span>
            <StatusIcons />
          </div>

          <PhoneHeader platform={platform} sender={senderLabel} palette={palette} />

          <div
            ref={threadRef}
            className="thin-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-2 pt-3"
            onScroll={() => {
              const el = threadRef.current;
              if (!el) return;
              pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 28;
            }}
          >
            {trimmed ? (
              <div>
                {platform === 'ios' ? (
                  <p
                    className="mb-2 text-center text-[11px] font-medium leading-none"
                    style={{ color: palette.sub }}
                  >
                    Hoje {clock}
                  </p>
                ) : null}
                <IncomingBubble platform={platform} palette={palette}>
                  <MessageText text={message} platform={platform} palette={palette} />
                </IncomingBubble>
                {platform === 'android' ? (
                  <p
                    className="mt-1 pl-0.5 text-[11px] leading-none"
                    style={{ color: palette.sub }}
                  >
                    {clock}
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center px-8 text-center">
                <p className="text-[13px] leading-relaxed" style={{ color: palette.sub }}>
                  A mensagem aparece aqui.
                </p>
              </div>
            )}
          </div>

          <ComposerBar platform={platform} palette={palette} />
        </div>
      </div>
    </section>
  );
}
